import * as i0 from '@angular/core';
import { signal, Directive, Input, inject, computed, booleanAttribute, Component, ContentChild, NgModule } from '@angular/core';
import { FormMultiItemMinMoveToDirective, FormMultiItemMaxMoveToDirective } from '@virginmediao2/momentum-angular/forms/form-navigation';
import { generateUniqueId, idAttribute, concatenateIdPipe, truthyOrNullPipe } from '@virginmediao2/momentum-angular/utilities';
import { IconComponent } from '@virginmediao2/momentum-angular/components/icon';

class FormMultiItemContextDirective {
    constructor() {
        this.$controlType = signal('group');
        this.$name = signal(generateUniqueId('form-group'));
        this.$labelId = signal(undefined);
        this.$descriptionId = signal(undefined);
        this.$error = signal(false);
        this.$errorId = signal(undefined);
    }
    set descriptionId(value) {
        this.$descriptionId.set(value);
    }
    get descriptionId() {
        return this.$descriptionId();
    }
    set error(value) {
        this.$error.set(value);
    }
    get error() {
        return this.$error();
    }
    set errorId(value) {
        this.$errorId.set(value);
    }
    get errorId() {
        return this.$errorId();
    }
    set name(value) {
        this.$name.set(value);
    }
    get name() {
        return this.$name();
    }
    set labelId(value) {
        this.$labelId.set(value);
    }
    get labelId() {
        return this.$labelId();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemContextDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.4", type: FormMultiItemContextDirective, isStandalone: true, selector: "[voFormMultiItemContext]", inputs: { descriptionId: "descriptionId", error: "error", errorId: "errorId", name: "name", labelId: "labelId" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemContextDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[voFormMultiItemContext]',
                    standalone: true,
                }]
        }], propDecorators: { descriptionId: [{
                type: Input
            }], error: [{
                type: Input
            }], errorId: [{
                type: Input
            }], name: [{
                type: Input
            }], labelId: [{
                type: Input
            }] } });

class FormMultiItemDescriptionDirective {
    constructor() {
        this.id = generateUniqueId('form-multi-item-description');
        this.context = inject(FormMultiItemContextDirective);
    }
    ngOnInit() {
        this.context.$descriptionId.set(this.id);
    }
    ngOnDestroy() {
        this.context.$descriptionId.set(undefined);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemDescriptionDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "17.3.4", type: FormMultiItemDescriptionDirective, isStandalone: true, selector: "[voFormMultiItemDescription]", inputs: { id: ["id", "id", idAttribute] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemDescriptionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[voFormMultiItemDescription]',
                    standalone: true,
                }]
        }], propDecorators: { id: [{
                type: Input,
                args: [{ transform: idAttribute }]
            }] } });

class FormMultiItemErrorDirective {
    constructor() {
        this.id = generateUniqueId('form-multi-item-error');
        this.context = inject(FormMultiItemContextDirective);
    }
    ngOnInit() {
        this.context.$errorId.set(this.id);
        this.context.$error.set(true);
    }
    ngOnDestroy() {
        this.context.$errorId.set(undefined);
        this.context.$error.set(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemErrorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "17.3.4", type: FormMultiItemErrorDirective, isStandalone: true, selector: "[voFormMultiItemError]", inputs: { id: ["id", "id", idAttribute] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemErrorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[voFormMultiItemError]',
                    standalone: true,
                }]
        }], propDecorators: { id: [{
                type: Input,
                args: [{ transform: idAttribute }]
            }] } });

class FormMultiItemLabelDirective {
    constructor() {
        this.id = generateUniqueId('form-multi-item-label');
        this.context = inject(FormMultiItemContextDirective);
    }
    ngOnInit() {
        this.context.$labelId.set(this.id);
    }
    ngOnDestroy() {
        this.context.$labelId.set(undefined);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "17.3.4", type: FormMultiItemLabelDirective, isStandalone: true, selector: "[voFormMultiItemLabel]", inputs: { id: ["id", "id", idAttribute] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[voFormMultiItemLabel]',
                    standalone: true,
                }]
        }], propDecorators: { id: [{
                type: Input,
                args: [{ transform: idAttribute }]
            }] } });

class FormMultiItemSeparatorDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemSeparatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.4", type: FormMultiItemSeparatorDirective, isStandalone: true, selector: "[voFormMultiItemSeparator]", host: { properties: { "attr.role": "\"separator\"", "attr.aria-hidden": "true" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemSeparatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[voFormMultiItemSeparator]',
                    standalone: true,
                    host: {
                        '[attr.role]': '"separator"',
                        '[attr.aria-hidden]': 'true',
                    },
                }]
        }] });

class FormMultiItemComponent {
    constructor() {
        this.#context = inject(FormMultiItemContextDirective);
        /** Sets the controls to be horizontal, or horizontal on larger screens */
        this.inline = false;
        this.ariaLabelledBy = computed(() => {
            const labelId = this.labelId();
            const descriptionId = this.descriptionId();
            return concatenateIdPipe(labelId, descriptionId);
        });
        this.ariaDescribedBy = computed(() => {
            const error = this.#context.$error();
            return error ? this.#context.$errorId() : null;
        });
        this.ariaInvalid = computed(() => truthyOrNullPipe(this.#context.$error()));
        this.controlType = computed(() => this.#context.$controlType());
        this.descriptionId = computed(() => this.#context.$descriptionId());
        this.errorId = computed(() => this.#context.$errorId());
        this.labelId = computed(() => this.#context.$labelId());
    }
    #context;
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.4", type: FormMultiItemComponent, isStandalone: true, selector: "vo-form-multi-item", inputs: { hideLabels: ["hideLabels", "hideLabels", booleanAttribute], marginBottom: ["marginBottom", "marginBottom", booleanAttribute], inline: "inline" }, queries: [{ propertyName: "ccFormMultiItemLabel", first: true, predicate: FormMultiItemLabelDirective, descendants: true }, { propertyName: "ccFormMultiItemDescription", first: true, predicate: FormMultiItemDescriptionDirective, descendants: true }, { propertyName: "ccFormMultiItemError", first: true, predicate: FormMultiItemErrorDirective, descendants: true }, { propertyName: "ccFormMultiItemSeparator", first: true, predicate: FormMultiItemSeparatorDirective, descendants: true }], hostDirectives: [{ directive: FormMultiItemContextDirective }], ngImport: i0, template: "<div\n    [attr.aria-labelledby]=\"ariaLabelledBy()\"\n    [attr.aria-describedby]=\"ariaDescribedBy()\"\n    [attr.aria-invalid]=\"ariaInvalid()\"\n    [attr.role]=\"controlType()\"\n    class=\"form-multi-item\"\n    [class.form-multi-item--has-hidden-labels]=\"hideLabels\"\n    [class.form-multi-item--margin-bottom]=\"marginBottom\"\n    data-testid=\"form-multi-item\">\n    <div\n        [id]=\"labelId()\"\n        class=\"form-multi-item__label\"\n        data-testid=\"form-multi-item-label\">\n        <ng-content select=\"[voFormMultiItemLabel]\" />\n    </div>\n\n    @if (ccFormMultiItemDescription) {\n    <div\n        [id]=\"descriptionId()\"\n        class=\"form-multi-item__description\"\n        data-testid=\"form-multi-item-description\">\n        <ng-content select=\"[voFormMultiItemDescription]\" />\n    </div>\n    }\n\n    <div\n        class=\"form-multi-item__controls\"\n        [class.form-multi-item__controls--inline]=\"inline === true\"\n        [class.form-multi-item__controls--inline-md-up]=\"inline === 'md-up'\"\n        [class.form-multi-item__controls__separator]=\"ccFormMultiItemSeparator\"\n        data-testid=\"form-multi-item-controls\">\n        <ng-content />\n    </div>\n\n    @if (ccFormMultiItemError) {\n    <div\n        [id]=\"errorId()\"\n        class=\"form-multi-item__error\"\n        data-testid=\"form-multi-item-error\">\n        <vo-icon\n            icon=\"X\"\n            class=\"form-multi-item__error-icon\" />\n\n        <div class=\"form-multi-item__error-message\">\n            <ng-content select=\"[voFormMultiItemError]\" />\n        </div>\n    </div>\n    }\n</div>\n", styles: [".form-multi-item{--controls-direction:column;--controls-gap:var(--padding-s);--form-multi-item-gap:var(--padding-xs);display:flex;gap:var(--form-multi-item-gap);flex-direction:column}.form-multi-item--has-hidden-labels{--form-multi-item-gap:0}.form-multi-item--has-hidden-labels .form-multi-item__label,.form-multi-item--has-hidden-labels .form-multi-item__description{clip:rect(0 0 0 0);clip-path:inset(50%);height:1px;width:1px;overflow:hidden;position:absolute;white-space:nowrap}.form-multi-item--margin-bottom{margin-bottom:var(--mo-sizing-md)}.form-multi-item__description{--override-font-weight:var(--font-weight-regular);--override-line-height:var(--line-height-200);--override-font-size:var(--font-size-200)}.form-multi-item__controls{display:flex;flex-wrap:wrap;flex-direction:var(--controls-direction);gap:var(--controls-gap)}.form-multi-item__controls--inline{--controls-direction:row;--controls-gap:var(--mo-sizing-md)}.form-multi-item__error{--override-type-color:var(--color-error-bg-dim);--override-line-height:var(--line-height-200);--override-font-size:var(--font-size-200);display:grid;grid-column-gap:var(--form-multi-item-gap);-moz-column-gap:var(--form-multi-item-gap);column-gap:var(--form-multi-item-gap);grid-template-columns:min-content 1fr;grid-template-areas:\"icon message\" \"icon message\" \". message\"}.form-multi-item__error-message{grid-area:message;display:flex;flex-direction:column;align-content:center;justify-content:center;place-content:center}.form-multi-item__error-icon{grid-area:icon;display:flex;align-content:center;justify-content:center;place-content:center;align-items:center;justify-items:center;place-items:center;color:var(--color-error-bg-dim)}.form-multi-item__controls__separator{--controls-direction:row;min-height:100%;display:flex;align-items:center}@media (min-width: 48em){.form-multi-item__controls--inline-md-up{--controls-direction:row;--controls-gap:var(--mo-sizing-md)}}\n"], dependencies: [{ kind: "component", type: IconComponent, selector: "vo-icon", inputs: ["aria-label", "aria-hidden", "size", "icon", "color", "iconRatio"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vo-form-multi-item', hostDirectives: [FormMultiItemContextDirective], imports: [IconComponent], standalone: true, template: "<div\n    [attr.aria-labelledby]=\"ariaLabelledBy()\"\n    [attr.aria-describedby]=\"ariaDescribedBy()\"\n    [attr.aria-invalid]=\"ariaInvalid()\"\n    [attr.role]=\"controlType()\"\n    class=\"form-multi-item\"\n    [class.form-multi-item--has-hidden-labels]=\"hideLabels\"\n    [class.form-multi-item--margin-bottom]=\"marginBottom\"\n    data-testid=\"form-multi-item\">\n    <div\n        [id]=\"labelId()\"\n        class=\"form-multi-item__label\"\n        data-testid=\"form-multi-item-label\">\n        <ng-content select=\"[voFormMultiItemLabel]\" />\n    </div>\n\n    @if (ccFormMultiItemDescription) {\n    <div\n        [id]=\"descriptionId()\"\n        class=\"form-multi-item__description\"\n        data-testid=\"form-multi-item-description\">\n        <ng-content select=\"[voFormMultiItemDescription]\" />\n    </div>\n    }\n\n    <div\n        class=\"form-multi-item__controls\"\n        [class.form-multi-item__controls--inline]=\"inline === true\"\n        [class.form-multi-item__controls--inline-md-up]=\"inline === 'md-up'\"\n        [class.form-multi-item__controls__separator]=\"ccFormMultiItemSeparator\"\n        data-testid=\"form-multi-item-controls\">\n        <ng-content />\n    </div>\n\n    @if (ccFormMultiItemError) {\n    <div\n        [id]=\"errorId()\"\n        class=\"form-multi-item__error\"\n        data-testid=\"form-multi-item-error\">\n        <vo-icon\n            icon=\"X\"\n            class=\"form-multi-item__error-icon\" />\n\n        <div class=\"form-multi-item__error-message\">\n            <ng-content select=\"[voFormMultiItemError]\" />\n        </div>\n    </div>\n    }\n</div>\n", styles: [".form-multi-item{--controls-direction:column;--controls-gap:var(--padding-s);--form-multi-item-gap:var(--padding-xs);display:flex;gap:var(--form-multi-item-gap);flex-direction:column}.form-multi-item--has-hidden-labels{--form-multi-item-gap:0}.form-multi-item--has-hidden-labels .form-multi-item__label,.form-multi-item--has-hidden-labels .form-multi-item__description{clip:rect(0 0 0 0);clip-path:inset(50%);height:1px;width:1px;overflow:hidden;position:absolute;white-space:nowrap}.form-multi-item--margin-bottom{margin-bottom:var(--mo-sizing-md)}.form-multi-item__description{--override-font-weight:var(--font-weight-regular);--override-line-height:var(--line-height-200);--override-font-size:var(--font-size-200)}.form-multi-item__controls{display:flex;flex-wrap:wrap;flex-direction:var(--controls-direction);gap:var(--controls-gap)}.form-multi-item__controls--inline{--controls-direction:row;--controls-gap:var(--mo-sizing-md)}.form-multi-item__error{--override-type-color:var(--color-error-bg-dim);--override-line-height:var(--line-height-200);--override-font-size:var(--font-size-200);display:grid;grid-column-gap:var(--form-multi-item-gap);-moz-column-gap:var(--form-multi-item-gap);column-gap:var(--form-multi-item-gap);grid-template-columns:min-content 1fr;grid-template-areas:\"icon message\" \"icon message\" \". message\"}.form-multi-item__error-message{grid-area:message;display:flex;flex-direction:column;align-content:center;justify-content:center;place-content:center}.form-multi-item__error-icon{grid-area:icon;display:flex;align-content:center;justify-content:center;place-content:center;align-items:center;justify-items:center;place-items:center;color:var(--color-error-bg-dim)}.form-multi-item__controls__separator{--controls-direction:row;min-height:100%;display:flex;align-items:center}@media (min-width: 48em){.form-multi-item__controls--inline-md-up{--controls-direction:row;--controls-gap:var(--mo-sizing-md)}}\n"] }]
        }], propDecorators: { hideLabels: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], marginBottom: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], inline: [{
                type: Input
            }], ccFormMultiItemLabel: [{
                type: ContentChild,
                args: [FormMultiItemLabelDirective]
            }], ccFormMultiItemDescription: [{
                type: ContentChild,
                args: [FormMultiItemDescriptionDirective]
            }], ccFormMultiItemError: [{
                type: ContentChild,
                args: [FormMultiItemErrorDirective]
            }], ccFormMultiItemSeparator: [{
                type: ContentChild,
                args: [FormMultiItemSeparatorDirective]
            }] } });

class FormMultiItemModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemModule, imports: [FormMultiItemComponent,
            FormMultiItemLabelDirective,
            FormMultiItemDescriptionDirective,
            FormMultiItemErrorDirective,
            FormMultiItemMinMoveToDirective,
            FormMultiItemMaxMoveToDirective,
            FormMultiItemSeparatorDirective], exports: [FormMultiItemComponent,
            FormMultiItemLabelDirective,
            FormMultiItemDescriptionDirective,
            FormMultiItemErrorDirective,
            FormMultiItemMinMoveToDirective,
            FormMultiItemMaxMoveToDirective,
            FormMultiItemSeparatorDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemModule, imports: [FormMultiItemComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: FormMultiItemModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        FormMultiItemComponent,
                        FormMultiItemLabelDirective,
                        FormMultiItemDescriptionDirective,
                        FormMultiItemErrorDirective,
                        FormMultiItemMinMoveToDirective,
                        FormMultiItemMaxMoveToDirective,
                        FormMultiItemSeparatorDirective,
                    ],
                    exports: [
                        FormMultiItemComponent,
                        FormMultiItemLabelDirective,
                        FormMultiItemDescriptionDirective,
                        FormMultiItemErrorDirective,
                        FormMultiItemMinMoveToDirective,
                        FormMultiItemMaxMoveToDirective,
                        FormMultiItemSeparatorDirective,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FormMultiItemComponent, FormMultiItemContextDirective, FormMultiItemDescriptionDirective, FormMultiItemErrorDirective, FormMultiItemLabelDirective, FormMultiItemModule, FormMultiItemSeparatorDirective };
